import React, { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { observer } from "mobx-react";
import QuantityControls from "./QuantityControls";
import VariationToggle from './VariationToggle';
import VariationSelect from './VariationSelect';

/**
 * Helper function to determine the variant type based on controller
 */
const getVariantType = (controller) => {
  switch (controller) {
    case "internal": return "internal";
    case "modal": return "modal";
    case "cart": return "cart";
    default: return "v2";
  }
};

const AddToCart = observer(({
  productVariations,
  productId,
  productStockStatus,
  handleRemoveFromCart,
  handleAddToCart,
  handleUpdateQuantity,
  quantity,
  productWeightQuantity,
  quantityController = "default",
  handleVariationChange,
  productType,
  selectedVariation,
  productDisplayType,
  setSelectedVariation,
  layout = "vertical", // 'vertical' or 'horizontal'
  isModal = false,
}) => {
  const t = useTranslations('Product');

  // Memoize published variations to avoid recalculating on every render
  const publishedVariations = useMemo(() => {
    return productVariations?.filter(variation => variation.status === "publish") || [];
  }, [productVariations]);

  useEffect(() => {
    if (productType === "variable" && !selectedVariation && publishedVariations.length > 0) {
      setSelectedVariation(publishedVariations[0]); // Set the first published variation by default
    }
  }, [productType, publishedVariations, selectedVariation, setSelectedVariation]);

  // Classes to manage vertical or horizontal layout
  const layoutClass = layout === 'horizontal' ? 'flex items-center md:items-start justify-center md:justify-start space-x-2' :
    'space-y-1 rtl:space-y-reverse';

  return (
    <div className={layoutClass}>
      {(productType === "variable" && selectedVariation) || productType === "simple" ? (
        <QuantityControls
          handleRemoveFromCart={handleRemoveFromCart}
          handleAddToCart={handleAddToCart}
          handleUpdateQuantity={handleUpdateQuantity}
          quantity={quantity}
          productWeightQuantity={productWeightQuantity}
          productDisplayType={productDisplayType}
          productId={productId}
          productStockStatus={productStockStatus}
          variant={getVariantType(quantityController)}
          disableInput
        />
      ) : null}

      {productType === "variable" && publishedVariations.length === 2 && (
        <VariationToggle
          variations={productVariations}
          selectedVariation={selectedVariation}
          setSelectedVariation={setSelectedVariation}
          t={t}
          layout={(layout === "horizontal" && "compact")}
          isModal={isModal}
        />
      )}

      {productType === "variable" && publishedVariations.length > 2 && (
        <VariationSelect
          variations={productVariations}
          productId={productId}
          selectedVariation={selectedVariation}
          handleVariationChange={handleVariationChange}
          t={t}
          layout={(layout === "horizontal" && "compact")}
        />
      )}
    </div>
  );
});

export default AddToCart;
